import React, { createContext, useContext, useState, useEffect } from 'react';
import AppConfig, { getLinkLanguage } from './DomainConfiguration';

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

// Translations for Dashboard pages
const translations = {
    en: {
        // Common
        select: 'Select',
        home: 'Home',
        hi: 'Hi',
        welcomeTo: 'Welcome To',
        welcomeToBloomlink: 'Welcome To Bloomlink!',

        // Dashboard sections
        createSession: 'Create Session',
        updateSession: 'Update Session',
        bookOnDemandSession: 'Book On-Demand Session',
        mySchedule: 'My Schedule',

        // Appointment related
        ongoingAppointments: 'Ongoing Appointments',
        upcomingSessions: 'Upcoming Sessions',
        recentSessions: 'Recent Sessions',
        wouldYouLikeToCreateAppointment: 'Would you like to create an Appointment?',
        create: 'Create',
        update: 'Update',

        // Session details
        sessionType: 'Session Type',
        ownerName: 'Owner Name',
        patientName: 'Patient Name',
        patient: 'Patient',
        patientNotes: 'Patient Notes',
        doctorName: 'Doctor Name',
        teamMemberName: 'Team Member Name',
        numberOfTeamMembers: '# of Team Members',
        date: 'Date',
        time: 'Time',
        channel: 'Channel',
        channelName: 'Channel Name',
        channelNotes: 'Channel Notes',
        sessionNotes: 'Session Notes',
        transcriptNotes: 'Transcript Notes',
        participants: 'Participants',
        recommendations: 'Recommendations',
        sessionNotesComplete: 'Session Notes Complete',
        noRecommendationsFound: 'No Recommendations are found',
        noSessionNotesFound: 'No Session Notes found',
        noTranscriptNotesFound: 'No Transcript Notes Found',
        noChannelNotesFound: 'No notes found',
        noChatFound: 'No chat found',
        recordingOn: 'Recording on',
        stopRecord: 'Stop Record',
        startRecord: 'Start Record',
        whiteboard: 'Whiteboard',
        maximize: 'Maximize',
        enableVideo: 'Enable Video',
        disableVideo: 'Disable Video',
        blurBackground: 'Blur Background',
        removeBackgroundBlur: 'Remove Background Blur',
        backgroundBlurFailed: 'Unable to change background blur',
        enableVoice: 'Enable Voice',
        disableVoice: 'Disable Voice',
        stopShare: 'Stop Share',
        screenShare: 'Screen Share',
        screenShareCancelled: 'Screen share cancelled',
        exitFullScreen: 'Exit Full Screen',
        fullScreen: 'Full Screen',
        toExitFullScreenPressEsc: 'To exit full screen, press Esc',
        raiseYourHand: 'Raise your hand',
        lowerYourHand: 'Lower your hand',
        participantsSessionNotes: 'Participants/Session Notes',
        undo: 'Undo',
        redo: 'Redo',
        clear: 'Clear',
        eraser: 'Eraser',
        element: 'Element',
        elementName: 'Element Name',
        dateOfSession: 'Date of Session',
        timeOfSession: 'Time of Session',
        additionalSummary: 'Additional Summary',
        uploadedImagesPDF: 'Uploaded Images/PDF',
        dateOfBirth: 'Date of Birth',
        recordings: 'Recordings',
        createdTime: 'Created Time',
        teamMembers: 'Team Members',
        guestMembers: 'Guest Members',
        externalInviteLink: 'External Invite Link',
        inviteLink: 'Invite Link',
        copyExternalInviteLink: 'Copy external invite link',
        addOrRemoveTeamMembers: 'Add or Remove Team Members',
        noOptions: 'No options',
        externalMembers: 'Add or Remove Guest Members',

        // Schedule
        startTime: 'Start Time',
        endTime: 'End Time',
        onDemand: 'On Demand',
        manageSchedule: 'Manage Schedule',
        noSchedulesFound: 'No Schedules Found',

        // Actions
        join: 'Join',
        joinCall: 'Join Call',
        incomingCall: 'Incoming Call',
        view: 'View',
        viewDetails: 'View Details',
        seeAll: 'See all',
        linkCopied: 'Link copied',
        showAll: 'Show All',
        showLess: 'Show Less',
        teamMemberParticipation: 'Team Participation',
        guestMemberParticipation: 'Guest Participation',
        edit: 'Edit',
        cancel: 'Cancel',
        save: 'Save',
        ok: 'Ok',
        enterYourDetails: 'Enter Your Details',
        firstName: 'First Name',
        lastName: 'Last Name',
        enterFirstName: 'Enter first Name',
        enterLastName: 'Enter last Name',
        enterAnEmailAddress: 'Enter an email address',
        enterEmailAddressError: 'Please enter an email address.',
        maxEmailAddressesError: 'You can add up to {count} email addresses only.',
        validEmailAddressError: 'Please enter a valid email address.',
        duplicateEmailAddressError: 'This email has already been added.',
        overlappingExternalEmailsError:
            'Same email cannot be used in both Invite Patient and Enter Email. Please keep it in only one section before saving.',
        addEmailBeforeSaveError:
            'Please add the email by clicking the "+" button or clear the input field.',
        atLeastOneEmailAddressError: 'At least one email address is mandatory.',
        failedToUpdateExternalMembers: 'Failed to update external members.',
        yes: 'Yes',
        no: 'No',
        clickYesToCompleteSessionNote: 'Click yes in order to complete the Session note.',
        reasonForCancellation: 'Reason For Cancellation',
        cancellationPlaceholder: 'Reason can be added upto 200 characters',
        endVisit: 'End',
        leaveSession: 'Leave Session',
        youHaveLeftTheSession: 'You have left the session',
        youMayCloseThisTab: 'You may close this tab now.',
        sessionHasEnded: 'This session has ended.',
        onceYouLeaveCall: 'Once you leave the',
        theCallWillBeEnded: 'the call will be ended.',
        areYouSureLeave: 'Are you sure you want to leave your',
        youMayResume: '? You may resume your session at any time until the',
        doctor: 'doctor',
        owner: 'owner',
        concludesIt: 'concludes it.',
        clickHereIssueCredit: 'Click here to issue',
        credit: 'credit',
        sessionExpireWarning: 'Your session will automatically expire within the next 5 minutes',
        sessionExpired: 'Your session has been automatically expired',
        sessionExpiredTitle: 'Session Expired',
        sessionExpiredLoginMessage: 'Please log in again to continue.',
        goToLogin: 'Go to Login',
        sessionNotStartedYet:
            "The session hasn't started yet. Please wait here, the host will begin shortly.",
        sessionEnded: 'Session Ended',
        your: 'Your',
        hasEndedTheSession: 'has ended the session',

        // Session Status
        enterSession: 'Enter session',
        scheduled: 'Scheduled',
        inCall: 'In Call',
        inWaitingRoom: 'In Waiting Room',
        completed: 'Completed',
        pending: 'Pending',
        notePending: 'Note Pending',
        notesPending: 'Notes Pending',
        cancelled: 'Cancelled',
        teamMemberMissed: 'Team Member Missed',
        ownerMissed: 'Owner Missed',
        doctorMissed: 'Doctor Missed',
        resumeYourSession: 'Resume your session',
        joinTheSession: 'Join the session',
        removed: 'Removed',

        // Summary
        teamMemberSummary: 'Team Member Summary',
        sessionSummary: 'Session Summary',
        soapNotSaved: 'Soap not Saved',
        imageNotSaved: 'Image not Saved',
        notesClearedClickSaveMessage: 'Notes cleared. Click Save to keep these changes.',
        viewSessionNotes: 'View Session Notes',
        viewChat: 'View Chat',
        viewFlagInfo: 'View Flag Info',
        documents: 'Documents',
        noDocumentsAvailable: 'No Documents Available',
        failedToFetchUserDetails: 'Failed to fetch user details',

        // Messages
        noAppointmentFound: 'No appointment found',
        noAppointmentsFound: 'No Appointments Found',
        noSessionsFound: 'No sessions found',
        notAssigned: 'Not Assigned',

        // Search
        search: 'Search',
        searchOwner: 'Search Owner',

        // Navigation
        sessions: 'Sessions',
        messages: 'Messages',
        team: 'Team',
        visit: 'Session',

        // NotFound page
        oopsPageNotFound: 'Oops! Page Not Found',
        sorryPageDoesntExist: "Sorry, the page you're looking for doesn't exist",
        backToHome: 'Back to Home',

        // Error page
        pageNotFound: 'Page Not Found',
        pageDoesNotExist: 'The page you are looking for does not exist.',
        goToHome: 'Go to Home',

        // Notifications
        allNotifications: 'All Notifications',
        markAllAsRead: 'Mark all as Read',
        noNotificationsFound: 'No Notifications Found',
        noSessionFound: 'No Session Found.',
        noTeamMembersFound: 'No Team Members Found.',
        noDataFound: 'No data found.',
        financialReportsDisabled:
            'Your administrator has disabled the Financial Reports. Please contact your admin for further details.',
        broadcastDisabled:
            'Your administrator has disabled the Live Stream Broadcast session related features. Please contact your admin for further details.',
        pleaseSelectDateForReport: 'Please select the date to generate report',
        pleaseClickGoButton: 'Please click go button and see reports',
        pleaseSelectLevel: 'Please select level in the drop-down above',

        // Messages Screen
        selectOrEnterName: 'Select or Enter Name',
        noRecordFound: 'No record Found',
        all: 'All',
        notRead: 'Not Read',
        noUnreadChatsFound: 'No unread chats found.',
        noUsersFound: 'No users found.',
        typing: 'Typing...',
        draft: 'Draft',
        chat: 'Chat',
        writeAMessage: 'Write a message',
        selectTeamMemberToStart: 'Select a Team Member to start a conversation.',
        selectOwnerToStart: 'Select an Owner to start a conversation.',
        you: 'You',
        bud: 'Bud',
        directMessage: 'Direct Message',
        to: 'To',
        everyone: 'Everyone',
        startMessageHere: 'Start a Message Here',

        // Loading messages
        pleaseWait: 'Please wait',
        reconnectingPleaseWait: 'Reconnecting... Please Wait',
        yourCallIsGettingInitiated: 'Your call is getting initiated, Please wait…',
        connectionIsGettingEstablished: 'Connection is getting established. Please wait...',
        videoStreamIsLoading:
            'Do not press the forward, backward, or any other key. Video stream is loading. Please wait..',
        callEndedSuccessfully: 'Call ended successfully',
        raiseNotReceived: 'Raise not received... try Again',
        broadcastStartedSuccessfully: 'Broadcast started successfully',
        broadcastNotStarted: 'Broadcast not started... try Again',
        screenshotSavedSuccessfully: 'Screenshot saved successfully',
        studentRemovedFromGroupSession: 'removed from group session',
        ownerEndedSession: 'Your Owner has ended the session',

        youHaveSessionScheduledWith: 'You have a session scheduled with',
        accountHasBeenVerifiedBy: 'Account has been verified by',
        yourSessionWith: 'Your session with',
        hasBeenCancelled: 'has been cancelled',
        reminderOnUpcomingSessionWith: 'Reminder on your upcoming session with',
        missedAppointmentWith: 'Missed an appointment with',
        reminderOnGroupSessionWith: 'Reminder on your group session with',
        yourGroupSessionWith: 'Your group session with',
        youHaveGroupSessionScheduledWith: 'You have a group session scheduled with',
        withTeamMembers: 'with Team Members',
        youHaveLiveStreamBroadcast: 'You have a live stream broadcast',
        sessionScheduledWithTeamMembers: 'session scheduled with Team Members',
        reminderOnLiveStreamBroadcast: 'Reminder on your live stream broadcast',
        sessionWithTeamMembers: 'session with Team Members',
        yourLiveStreamBroadcast: 'Your live stream broadcast',
        sessionWithTeamMembersHasBeenCancelled: 'session with Team Members has been cancelled',
        youHaveTeamMember: 'You have a Team Member',
        admin: 'Admin',
        needsMoreInformationOnFlaggedSession: 'needs more information on the flagged session',
        hasTakenActionOnFlaggedSession: 'has taken action on the flagged session',
        referredYouForThisSessionWith: 'referred you for this session with',
        youHaveBeenReferredBy: 'You have been referred by',
        forChannel: 'for',
        refundRaisedSuccessfully: 'Refund raised successfully',
        sessionScheduledWithOwner: 'session scheduled with Owner',
        sessionWithOwner: 'session with Owner',
        yourGroupSessionWithHasBeenCancelled: 'Your group session with',
        reminderOnYourSessionWith: 'Reminder on your session with',
        referredYouTo: 'referred you to',
        forThisSession: 'for this session',
        referred: 'referred',

        absent: 'Absent',
        present: 'Present',
        searchTeamMembersName: 'Search Team Members Name',
        searchTeamMemberName: 'Search and select by team member name',
        searchGuestMemberName: 'Search and select by guest member name',
        selectTeamMember: 'Select Team Member',
        selectTeamMembers: 'Select Team Members',
        group: 'Group',
        groupSession: 'Group Session',
        conferenceSession: 'Conference Session',
        liveStreamBroadcast: 'Live Stream Broadcast',
        selectElement: 'Select Element',
        summaryPlaceholder: 'Summary can be added upto 500 Characters',
        uploadInstructions:
            'Upload images or pdf of size upto 10MB in the file format .png, .jpeg, .jpg, .pdf',
        indicatesRequiredFields: 'indicates Required Fields',
        viewSummary: 'View Summary',
        sortBy: 'Sort By',
        allSessions: 'All Sessions',
        todaySession: 'Today Session',
        previousSession: 'Previous Session',
        customDate: 'Custom Date',
        cancelSession: 'Cancel Session',
        submit: 'Submit',
        referSession: 'Refer Session',
        selectEducator: 'Select Educator',
        notes: 'Notes',
        flagSession: 'Flag Session',
        selectCategory: 'Select Category',
        otherReason: 'Other Reason',
        reason: 'Reason',
        createReferral: 'Create Referral',
        myReferrals: 'My Referrals',
        completedReferrals: 'Completed Referrals',
        selectSubject: 'Select Subject',
        selectSubcategory: 'Select Subcategory',
        selectStudent: 'Select Student',
        searchStudent: 'Search Student',
        searchEducator: 'Search Educator',
        createdBy: 'Created By',
        dateAndTime: 'Date and Time',
        sessionIsRequestedForCancelOrReschedule: 'Session is Requested for Cancel or Reschedule',

        // CreateSession
        oneToOne: 'One to One',
        classroom: 'Classroom',
        appointment: 'Appointment',
        selectOwner: 'Select Owner',
        searchOwnerName: 'Search and select by Owner name',
        onDemandAppointment: 'On Demand Appointment',
        subject: 'Subject',
        subcategory: 'Subcategory',
        educator: 'Educator',
        selectDate: 'Select Date',
        selectTime: 'Select Time',
        selectSlot: 'Select Slot',
        topic: 'Topic',
        comments: 'Comments',
        attachFiles: 'Attach Files',
        uploadFiles: 'Upload Files',
        availableSlots: 'Available Slots',
        noSlotsAvailable: 'No Slots Available',
        pleaseSelectDate: 'Please Select Date',
        selectLevel: 'Select Level',
        primaryLevel3: 'Primary Level 3',
        summaryPlaceholder300: 'Summary can be added upto 300 Characters',
        uploadInstructions5MB:
            'Upload images or pdf of size upto 5MB in the file format .png, .jpeg, .jpg, .pdf',
        addNotes: 'Add Notes',
        studentHistory: 'Student History',
        addNotesHistory: 'Add Notes History',
        tutorDetail: 'Tutor Detail',
        sessionDetail: 'Session Detail',
        sessionHistory: 'Session History',
        subjectName: 'Subject Name',
        visitCount: 'Visit Count',
        educatorName: 'Educator Name',
        educatorDetail: 'Educator Detail',
        loading: 'Loading...',
        close: 'Close',
        sessionDate: 'Session Date',
        status: 'Status',
        inPerson: 'In Person',
        virtuOwl: 'VirtuOwl',
        selectChannel: 'Select Channel',
        searchChannel: 'Search Channel',
        language: 'Language',
        selectLanguage: 'Select Language',
        english: 'English',
        spanish: 'Spanish',

        // AddNotes Modal
        summary: 'Summary',
        summaryPlaceholder500: 'Summary can be added up to 500 characters',
        materialImagesPDF: 'Material Images/PDF',
        uploadInstructions10MB:
            'Upload images or pdf of size upto 10MB in the file format .png, .jpeg, .jpg, .pdf',
        selectAnyDate: 'Select any date',

        // StudentSummary
        memberSummary: 'Member Summary',
        memberName: 'Member Name',
        createASession: 'Create a Session',
        viewMemberHistory: 'View Member History',
        viewMemberNotes: 'View Member Notes',
        sessionTime: 'Session Time',
        noDatasFound: 'No datas found',
        reportedBy: 'Reported by',
        timeOfNotesAdded: 'Time of Notes Added',
        comment: 'Comment',
        attachment: 'Attachment',
        noMaterialImagesFound: 'No Material Images/PDF found',
    },
    es: {
        // Common
        select: 'Seleccionar',
        home: 'Inicio',
        hi: 'Hola',
        welcomeTo: 'Bienvenido a',
        welcomeToBloomlink: '¡Bienvenido a Bloomlink!',

        // Dashboard sections
        createSession: 'Crear sesión',
        updateSession: 'Actualizar sesión',
        bookOnDemandSession: 'Reservar Sesión Bajo Demanda',
        mySchedule: 'Mi Horario',

        // Appointment related
        ongoingAppointments: 'Citas en curso',
        upcomingSessions: 'Sesiones programadas',
        recentSessions: 'Sesiones recientes',
        wouldYouLikeToCreateAppointment: '¿Desea crear una cita?',
        create: 'Crear',
        update: 'Actualizar',

        // Session details
        sessionType: 'Tipo de Sesión',
        ownerName: 'Nombre del Propietario',
        patientName: 'Paciente',
        patient: 'Paciente',
        patientNotes: 'Notas del paciente',
        doctorName: 'Nombre del Doctor',
        teamMemberName: 'Nombre del profesional',
        numberOfTeamMembers: '# de Miembros del Equipo',
        date: 'Fecha',
        time: 'Hora',
        channel: 'Canal',
        channelName: 'Canal',
        channelNotes: 'Notas del canal',
        sessionNotes: 'Notas de la Sesión',
        transcriptNotes: 'Notas de la transcripción',
        participants: 'Participantes',
        recommendations: 'Recomendaciones',
        sessionNotesComplete: 'Marcar notas como completadas',
        noRecommendationsFound: 'No se encontraron recomendaciones',
        noSessionNotesFound: 'No se encontraron Notas de Sesión',
        noTranscriptNotesFound: 'No se encontraron notas de transcripción',
        noChannelNotesFound: 'No se encontraron notas',
        noChatFound: 'No se encontró chat',
        recordingOn: 'Grabación en curso',
        stopRecord: 'Detener grabación',
        startRecord: 'Iniciar grabación',
        whiteboard: 'Pizarra',
        maximize: 'Maximizar',
        enableVideo: 'Activar video',
        disableVideo: 'Desactivar video',
        blurBackground: 'Desenfocar fondo',
        removeBackgroundBlur: 'Quitar desenfoque de fondo',
        backgroundBlurFailed: 'No se pudo cambiar el desenfoque de fondo',
        enableVoice: 'Activar voz',
        disableVoice: 'Desactivar voz',
        stopShare: 'Detener compartir',
        screenShare: 'Compartir pantalla',
        screenShareCancelled: 'Se canceló compartir pantalla',
        exitFullScreen: 'Salir de pantalla completa',
        fullScreen: 'Pantalla completa',
        toExitFullScreenPressEsc: 'Para salir de pantalla completa, presione Esc',
        raiseYourHand: 'Levanta tu mano',
        lowerYourHand: 'Baja tu mano',
        participantsSessionNotes: 'Participantes/Notas de sesión',
        undo: 'Deshacer',
        redo: 'Rehacer',
        clear: 'Limpiar',
        eraser: 'Borrador',
        element: 'Elemento',
        elementName: 'Nombre del Elemento',
        dateOfSession: 'Fecha',
        timeOfSession: 'Hora de la sesión',
        additionalSummary: 'Nota adicional',
        uploadedImagesPDF: 'Archivos adjuntos',
        dateOfBirth: 'Fecha de nacimiento',
        recordings: 'Grabaciones',
        createdTime: 'Fecha de creación',

        teamMembers: 'Miembros del equipo',
        guestMembers: 'Miembros invitados',
        externalInviteLink: 'Enlace de invitación externa',
        inviteLink: 'Enlace de invitación',
        copyExternalInviteLink: 'Copiar enlace de invitación externa',
        addOrRemoveTeamMembers: 'Agregar o quitar participantes',
        noOptions: 'Sin opciones',
        externalMembers: 'Agregar o quitar miembros invitados',

        // Schedule
        startTime: 'Hora de Inicio',
        endTime: 'Hora de término',
        onDemand: 'Bajo Demanda',
        manageSchedule: 'Administrar Horario',
        noSchedulesFound: 'No se Encontraron Horarios',

        // Actions
        join: 'Unirse',
        joinCall: 'Unirse a la Llamada',
        incomingCall: 'Llamada Entrante',
        view: 'Ver',
        viewDetails: 'Ver Detalles',
        seeAll: 'Ver todo',
        linkCopied: 'Enlace copiado',
        showAll: 'Mostrar Todo',
        showLess: 'Mostrar Menos',
        teamMemberParticipation: 'Participación del Equipo',
        guestMemberParticipation: 'Participación de Invitados',
        edit: 'Editar',
        cancel: 'Cancelar',
        save: 'Guardar',
        ok: 'Ok',
        enterYourDetails: 'Ingrese sus datos',
        firstName: 'Nombre(s)',
        lastName: 'Apellido(s)',
        enterFirstName: 'Ingresa tu nombre',
        enterLastName: 'Ingresa tu apellido',
        enterAnEmailAddress: 'Ingrese una dirección de correo electrónico',
        enterEmailAddressError: 'Ingrese una dirección de correo electrónico.',
        maxEmailAddressesError:
            'Puede agregar hasta {count} direcciones de correo electrónico solamente.',
        validEmailAddressError: 'Ingrese una dirección de correo electrónico válida.',
        duplicateEmailAddressError: 'Esta dirección de correo electrónico ya fue agregada.',
        overlappingExternalEmailsError:
            'El mismo correo no puede usarse en Invitar paciente y Agregar correo. Déjalo en una sola sección antes de guardar.',
        addEmailBeforeSaveError:
            'Agregue el correo electrónico haciendo clic en "+" o borre el campo.',
        atLeastOneEmailAddressError:
            'Es obligatorio agregar al menos una dirección de correo electrónico.',
        failedToUpdateExternalMembers: 'No se pudieron actualizar los miembros invitados.',
        yes: 'Sí',
        no: 'No',
        clickYesToCompleteSessionNote: '¿Desea marcar la nota de la sesión como completada?',
        reasonForCancellation: 'Motivo de la cancelación',
        cancellationPlaceholder:
            'Puedes registrar el motivo de la cancelación (máx. 200 caracteres)',
        endVisit: 'Finalizar',
        leaveSession: 'Abandonar la sesión',
        youHaveLeftTheSession: 'Has abandonado la sesión',
        youMayCloseThisTab: 'Ya puedes cerrar esta pestaña.',
        sessionHasEnded: 'Esta sesión ha finalizado.',
        onceYouLeaveCall: 'Al salir de la',
        theCallWillBeEnded: ' la llamada finalizará automáticamente.',
        areYouSureLeave: '¿Estás seguro de que quieres abandonar tu',
        youMayResume: '? Puedes reanudar tu sesión en cualquier momento hasta que el',
        doctor: 'médico',
        owner: 'propietario',
        concludesIt: 'la concluya.',
        clickHereIssueCredit: 'Haz clic aquí para emitir',
        credit: 'crédito',
        sessionExpireWarning: 'Tu sesión expirará automáticamente en los próximos 5 minutos',
        sessionExpired: 'Tu sesión ha expirado automáticamente',
        sessionExpiredTitle: 'Sesión expirada',
        sessionExpiredLoginMessage: 'Por favor, inicie sesión nuevamente para continuar.',
        goToLogin: 'Ir a iniciar sesión',
        sessionNotStartedYet:
            'La sesión aún no ha comenzado. Por favor espera aquí, el anfitrión comenzará en breve.',
        sessionEnded: 'Sesión Finalizada',
        your: 'Tu',
        hasEndedTheSession: 'ha finalizado la sesión',

        // Session Status
        enterSession: 'Entrar a la sesión',
        scheduled: 'Programada',
        inCall: 'En Llamada',
        inWaitingRoom: 'En Sala de Espera',
        completed: 'Completada',
        pending: 'Pendiente',
        notePending: 'Nota Pendiente',
        notesPending: 'Notas Pendientes',
        cancelled: 'Cancelada',
        teamMemberMissed: 'Profesional no se presentó',
        ownerMissed: 'Propietario no se presentó',
        resumeYourSession: 'Reanudar su sesión',
        joinTheSession: 'Unirse a la sesión',
        removed: 'Eliminado',
        doctorMissed: 'Doctor no se presentó',

        // Summary
        teamMemberSummary: 'Resumen del miembro del Equipo',
        sessionSummary: 'Resumen de la Sesión',
        soapNotSaved: 'Soap no guardado',
        imageNotSaved: 'Imagen no guardada',
        notesClearedClickSaveMessage:
            'Notas borradas. Haz clic en Guardar para mantener estos cambios.',
        viewSessionNotes: 'Ver notas de la sesión',
        viewChat: 'Ver Chat',
        viewFlagInfo: 'Ver Información de Bandera',
        documents: 'Documentos',
        noDocumentsAvailable: 'No Hay Documentos Disponibles',
        failedToFetchUserDetails: 'Error al obtener detalles del usuario',

        // Messages
        noAppointmentFound: 'No hay citas en curso',
        noAppointmentsFound: 'No se Encontraron Citas',
        noSessionsFound: 'No hay sesiones registradas',
        notAssigned: 'No Asignado',

        // Search
        search: 'Buscar mensajes',
        searchOwner: 'Buscar mensajes Propietario',

        // Navigation
        sessions: 'Sesiones',
        messages: 'Mensajes',
        team: 'Equipo',
        visit: 'Sesión',

        // Messages Screen
        selectOrEnterName: 'Seleccionar o ingresar nombre',
        noRecordFound: 'No se Encontró Registro',
        all: 'Todos',
        notRead: 'No leídos',
        noUnreadChatsFound: 'No se encontraron chats no leídos.',
        noUsersFound: 'No se encontraron miembros del equipo.',
        typing: 'Escribiendo...',
        draft: 'Borrador',
        chat: 'Chat',
        writeAMessage: 'Escribe un mensaje',
        selectTeamMemberToStart:
            'Selecciona a un miembro del equipo para iniciar una conversación.',
        selectOwnerToStart: 'Selecciona un Propietario para iniciar una conversación.',
        you: 'Tú',
        bud: 'Bud',
        directMessage: 'Mensaje directo',
        to: 'a',
        everyone: 'Todos',
        startMessageHere: 'Escribe un mensaje aquí',
        student: 'miembro del equipo',

        // Loading messages
        pleaseWait: 'Por favor espere',
        reconnectingPleaseWait: 'Reconectando... Por Favor Espere',
        yourCallIsGettingInitiated: 'Su llamada se está iniciando, por favor espere…',
        connectionIsGettingEstablished: 'Se está estableciendo la conexión. Por favor espere...',
        videoStreamIsLoading:
            'No presione las teclas de avanzar, retroceder ni ninguna otra tecla. La transmisión de video se está cargando. Por favor espere..',
        callEndedSuccessfully: 'Llamada finalizada exitosamente',
        raiseNotReceived: 'Levantamiento no recibido... intente de nuevo',
        broadcastStartedSuccessfully: 'Transmisión iniciada exitosamente',
        broadcastNotStarted: 'Transmisión no iniciada... intente de nuevo',
        screenshotSavedSuccessfully: 'Captura de pantalla guardada exitosamente',
        studentRemovedFromGroupSession: 'eliminado de la Sesión grupal',
        ownerEndedSession: 'Su Propietario ha finalizado la sesión',
        // NotFound page
        oopsPageNotFound: '¡Ups! Página No Encontrada',
        sorryPageDoesntExist: 'Lo sentimos, la página que buscas no existe',
        backToHome: 'Volver al Inicio',

        // Error page
        pageNotFound: 'Página No Encontrada',
        pageDoesNotExist: 'La página que buscas no existe.',
        goToHome: 'Ir al Inicio',

        // Notifications
        allNotifications: 'Notificaciones',
        markAllAsRead: 'Marcar todo como leído',
        noNotificationsFound: 'No hay notificaciones',
        noSessionFound: 'No hay sesiones registradas',
        noTeamMembersFound: 'No se Encontraron Miembros del Equipo.',
        noDataFound: 'No se encontraron datos.',
        youHaveSessionScheduledWith: 'Tiene una sesión programada con ',
        accountHasBeenVerifiedBy: 'La cuenta ha sido verificada por',
        yourSessionWith: 'Su sesión con',
        hasBeenCancelled: 'ha sido cancelada',
        reminderOnUpcomingSessionWith: 'Recordatorio de su próxima sesión con ',
        missedAppointmentWith: 'Cita perdida con',
        reminderOnGroupSessionWith: 'Recordatorio de su Sesión grupal con ',

        yourGroupSessionWith: 'Su Sesión grupal con',
        youHaveGroupSessionScheduledWith: 'Tiene una Sesión grupal programada ',
        withTeamMembers: 'con Miembros del Equipo',
        youHaveLiveStreamBroadcast: 'Tiene una transmisión en vivo',
        sessionScheduledWithTeamMembers: 'sesión programada con Miembros del Equipo',
        reminderOnLiveStreamBroadcast: 'Recordatorio de su transmisión en vivo',
        sessionWithTeamMembers: 'sesión con Miembros del Equipo',
        yourLiveStreamBroadcast: 'Su transmisión en vivo',
        sessionWithTeamMembersHasBeenCancelled: 'sesión con Miembros del Equipo ha sido cancelada',
        youHaveTeamMember: 'Tiene un miembro del equipo',
        admin: 'Administrador',
        needsMoreInformationOnFlaggedSession: 'necesita más información sobre la sesión marcada',
        hasTakenActionOnFlaggedSession: 'ha tomado medidas sobre la sesión marcada',
        referredYouForThisSessionWith: 'te refirió para esta sesión con',
        youHaveBeenReferredBy: 'Has sido referido por',
        forChannel: 'para',
        refundRaisedSuccessfully: 'Reembolso realizado exitosamente',
        sessionScheduledWithOwner: 'sesión programada con Propietario',
        sessionWithOwner: 'sesión con Propietario',
        yourGroupSessionWithHasBeenCancelled: 'Su Sesión grupal con',
        reminderOnYourSessionWith: 'Recordatorio de su sesión con',
        referredYouTo: 'te refirió a',
        forThisSession: 'para esta sesión',
        referred: 'refirió',

        absent: 'Ausencia',
        present: 'Presente',
        searchTeamMembersName: 'Buscar miembro por nombre',
        searchTeamMemberName: 'Buscar profesional',
        searchGuestMemberName: 'Buscar y seleccionar por miembro invitado',
        selectTeamMember: 'Seleccionar miembro del equipo',
        selectTeamMembers: 'Seleccionar Miembros del Equipo',
        group: 'Grupo',
        groupSession: 'Sesión grupal',
        conferenceSession: 'Sesión de conferencia',
        liveStreamBroadcast: 'Transmisión en Vivo',
        selectElement: 'Seleccionar elemento',
        summaryPlaceholder: 'Agregar resumen (hasta 500 caracteres)',
        uploadInstructions: 'Adjunta imágenes (png, jpeg, jpg) o PDFs (máx. 10 MB)',
        indicatesRequiredFields: 'Campos obligatorios',
        viewSummary: 'Ver Resumen',
        sortBy: 'Ordenar por',
        allSessions: 'Todas las sesiones',
        todaySession: 'Sesiones para hoy',
        previousSession: 'Sesiones anteriores',
        customDate: 'Fecha específica',
        cancelSession: 'Cancelar Sesión',
        submit: 'Enviar',
        referSession: 'Referir Sesión',
        selectEducator: 'Seleccionar Educador',
        notes: 'Notas',
        flagSession: 'Marcar Sesión',
        selectCategory: 'Seleccionar Categoría',
        otherReason: 'Otro Motivo',
        reason: 'Motivo',
        createReferral: 'Crear Referencia',
        myReferrals: 'Mis Referencias',
        completedReferrals: 'Referencias Completadas',
        selectSubject: 'Seleccionar Materia',
        selectSubcategory: 'Seleccionar Subcategoría',
        selectStudent: 'Seleccionar Estudiante',
        searchStudent: 'Buscar Estudiante',
        searchEducator: 'Buscar Educador',
        createdBy: 'Creado por',
        dateAndTime: 'Fecha y hora',
        sessionIsRequestedForCancelOrReschedule:
            'La sesión está solicitada para cancelar o reprogramar',

        // CreateSession
        oneToOne: 'Sesión individual',
        classroom: 'Aula',
        appointment: 'Cita',
        selectOwner: 'Seleccionar Propietario',
        searchOwnerName: 'Buscar y seleccionar por nombre del propietario',
        onDemandAppointment: 'Cita Bajo Demanda',
        subject: 'Materia',
        subcategory: 'Subcategoría',
        educator: 'Educador',
        selectDate: 'Selecciona una fecha',
        selectTime: 'Seleccionar Hora',
        selectSlot: 'Seleccionar Espacio',
        topic: 'Tema',
        comments: 'Comentarios',
        attachFiles: 'Adjuntar Archivos',
        uploadFiles: 'Adjuntar archivos',
        availableSlots: 'Espacios Disponibles',
        noSlotsAvailable: 'No Hay Espacios Disponibles',
        pleaseSelectDate: 'Por favor Seleccione Fecha',
        selectLevel: 'Seleccionar Nivel',
        primaryLevel3: 'Nivel Primario 3',
        summaryPlaceholder300: 'Se puede agregar un resumen de hasta 300 caracteres',
        uploadInstructions5MB:
            'Sube imágenes o PDF de hasta 5 MB en formato .png, .jpeg, .jpg, .pdf',
        addNotes: 'Agregar Notas',
        studentHistory: 'Historial del Estudiante',
        addNotesHistory: 'Historial de Notas',
        tutorDetail: 'Detalle del Tutor',
        sessionDetail: 'Detalle de la Sesión',
        sessionHistory: 'Historial de Sesiones',
        subjectName: 'Nombre de la Materia',
        visitCount: 'Número de Visitas',
        educatorName: 'Nombre del Educador',
        educatorDetail: 'Detalle del Educador',
        loading: 'Cargando...',
        close: 'Cerrar',
        sessionDate: 'Fecha de la Sesión',
        status: 'Estado',
        inPerson: 'Sesión presencial',
        virtuOwl: 'VirtuOwl',
        selectChannel: 'Seleccionar Canal',
        searchChannel: 'Buscar Canal',
        language: 'Idioma',
        selectLanguage: 'Seleccionar idioma',
        english: 'Inglés',
        spanish: 'Español',

        // AddNotes Modal
        summary: 'Resumen',
        summaryPlaceholder500: 'Agregar resumen (hasta 500 caracteres)',
        materialImagesPDF: 'Archivos adjuntos',
        uploadInstructions10MB: 'Adjunta imágenes (png, jpeg, jpg) o PDFs (máx. 10 MB)',
        selectAnyDate: 'Selecciona una fecha',

        // StudentSummary
        memberSummary: 'Resumen del miembro',
        memberName: 'Nombre',
        createASession: 'Crear una Sesión',
        viewMemberHistory: 'Historial',
        viewMemberNotes: 'Notas',
        sessionTime: 'Fecha y hora',
        noDatasFound: 'No se encontraron datos',
        reportedBy: 'Reportado por',
        timeOfNotesAdded: 'Fecha y hora en que se agregaron',
        comment: 'Comentario',
        attachment: 'Archivo adjunto',
        noMaterialImagesFound: 'No se encontraron Archivos adjuntos',
    },
};

export const getTranslation = (key, lang) => {
    const preferredLanguage =
        AppConfig.isHealthCare === true
            ? localStorage.getItem('preferredLanguage') || localStorage.getItem('user_language')
            : null;
    const resolvedLang =
        lang || getLinkLanguage() || preferredLanguage || AppConfig.appLanguage || 'en';
    return translations[resolvedLang]?.[key] || translations['en'][key] || key;
};

export const LanguageProvider = ({ children }) => {
    // A third party's invite link language outranks anything left in this
    // browser by a signed-in user - see getLinkLanguage().
    const getStoredLanguage = () =>
        getLinkLanguage() ||
        (AppConfig.isHealthCare === true
            ? localStorage.getItem('preferredLanguage') || localStorage.getItem('user_language')
            : null) ||
        AppConfig.appLanguage ||
        'en';

    const [language, setLanguage] = useState(getStoredLanguage());

    useEffect(() => {
        // Update language if localStorage or AppConfig changes
        const storedLang = getStoredLanguage();
        if (storedLang !== language) {
            setLanguage(storedLang);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AppConfig.appLanguage]);

    const t = (key) => {
        return translations[language]?.[key] || translations['en'][key] || key;
    };

    const changeLanguage = (lang) => {
        if (translations[lang]) {
            setLanguage(lang);
        }
    };

    return (
        <LanguageContext.Provider value={{ language, changeLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;
